/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';

import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button, Chip, ChipGroup, Grid, PageGridContainer, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { ShowMoreGridSection } from 'bundles/cds-labs/components';
import type { ConsumerPathwayRolePageDataContextType } from 'bundles/unified-career-academy/contexts/ConsumerPathwayRolePageDataContext';
import type { PathwaySpecializationOnRdp } from 'bundles/unified-career-academy/hooks/useCareerRoleData';
import PathwayCard from 'bundles/unified-career-academy/module-renderers/pathways-module/PathwayCard';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import { getPathwaysUserBasedVariant } from 'bundles/unified-common/utils/showPathways';

import _t from 'i18n!nls/career-academy';

type Props = {
  pageData: ConsumerPathwayRolePageDataContextType;
};

const styles = {
  section: css`
    margin-top: var(--cds-spacing-600);
    margin-bottom: var(--cds-spacing-600);
  `,
  sectionTitle: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  chipGroup: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  pathwaysContainer: css`
    margin-top: calc(-1 * (var(--cds-spacing-300) + var(--cds-spacing-50)));
    margin-bottom: calc(-1 * var(--cds-spacing-300));
  `,
  pathways: css`
    > ul {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
};

const getProficiencyLevelLabel = (level: PathwayLevel) => {
  switch (level) {
    case PathwayLevel.Beginner:
      return _t('Beginner');
    case PathwayLevel.Intermediate:
      return _t('Intermediate');
    case PathwayLevel.Advanced:
      return _t('Advanced');
    default:
      return '';
  }
};

export const getLevelFromUrl = (
  proficiencyLevels: PathwayLevel[],
  query?: Record<string, string>
): PathwayLevel | undefined => {
  return proficiencyLevels.find((level) => level.toLowerCase() === query?.level);
};

const PathwaysModuleRenderer = ({ pageData }: Props) => {
  const { pathways, name: roleName, slug: roleSlug } = pageData?.careerRoleData ?? {};
  const { query } = useLocation();
  const showFullPathwayExperience = isAuthenticatedUser() && getPathwaysUserBasedVariant();
  const track = useTracker();

  const groupedPathways = useMemo(() => {
    const pathwayGroups = new Map<PathwayLevel, PathwaySpecializationOnRdp[]>([
      [PathwayLevel.Beginner, []],
      [PathwayLevel.Intermediate, []],
      [PathwayLevel.Advanced, []],
    ]);

    pathways?.entities?.forEach((pathway: PathwaySpecializationOnRdp) => {
      if (!pathway || !pathway.proficiencyLevel) {
        return;
      }

      pathwayGroups.get(pathway.proficiencyLevel)?.push(pathway);
    });

    // Remove prociency levels with no pathways
    for (const level of pathwayGroups.keys()) {
      if (pathwayGroups.get(level)?.length === 0) {
        pathwayGroups.delete(level);
      }
    }

    return pathwayGroups;
  }, [pathways]);

  const proficiencyLevels = Array.from(groupedPathways.keys());

  const [selectedLevel, setSelectedLevel] = React.useState<PathwayLevel>(() => {
    return getLevelFromUrl(proficiencyLevels, query) ?? proficiencyLevels[0];
  });

  // If there are no pathways, don't render the module
  if (groupedPathways.size === 0) {
    return null;
  }

  const renderFilterChips = () => {
    // If there is only one proficiency level, don't render the filter
    if (groupedPathways.size === 1) {
      return null;
    }

    const chips = proficiencyLevels.map((level) => {
      const handleSelect = () => {
        track('click_button', {
          button: { name: 'collections_filter_chip', linkName: `collection_filter_chip_${level.toLowerCase()}` },
        });

        setSelectedLevel(level);
      };
      return (
        <Chip key={level} onClick={handleSelect} selected={level === selectedLevel} data-testid="level">
          {getProficiencyLevelLabel(level)}
        </Chip>
      );
    });

    return (
      <div css={styles.chipGroup}>
        <ChipGroup aria-label={_t('Filter by proficiency level')}>{chips}</ChipGroup>
      </div>
    );
  };

  const handleExpandButtonClick = (isExpanded: boolean) => {
    track(isExpanded ? 'expand_carousel' : 'collapse_carousel', {
      carousel: {
        id: pathways?.id ?? '',
        name: roleSlug?.replace(/-/g, '_'),
        recommender: {
          hash: pathways?.recommenderConfigurationHash || '',
          context: pathways?.context,
        },
      },
    });
  };

  const renderPathways = (level: PathwayLevel) => {
    const pathwaysToShow = groupedPathways.get(level) as PathwaySpecializationOnRdp[];

    return (
      <div css={styles.pathwaysContainer} data-testid="recommended-pathways">
        <ShowMoreGridSection
          itemsGridConfig={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          defaultRows={
            // We need to render all the cards to ensure that the pathway card the learner previously clicked on is
            // displayed upon page refresh after they log in.
            // This way, we can trigger the enrollment modal for learners to continue their enrollment flow.
            isAuthenticatedUser() && !!getLevelFromUrl(proficiencyLevels, query) && !!query.action
              ? pathwaysToShow.length
              : 2
          }
          showMoreAmount={pathwaysToShow.length - 2}
          items={pathwaysToShow}
          renderItem={({ item, itemIndex }) => (
            <PathwayCard
              {...item}
              name={getProductTitle(item.name, item.productVariant, item.slug)}
              key={item.id}
              showTopRecommendedBadge={showFullPathwayExperience && itemIndex === 0}
              showCoach={showFullPathwayExperience && itemIndex === 0}
              trackingData={{
                productCard: {
                  index: itemIndex,
                  recommender: {
                    hash: pathways?.recommenderConfigurationHash,
                    context: pathways?.context,
                  },
                },
                carousel: {
                  id: pageData?.careerRoleData?.id,
                },
              }}
              alternativePathways={pathwaysToShow}
              role={roleSlug ? { name: roleName ?? '', slug: roleSlug } : {}}
            />
          )}
          onExpanded={({ isExpanded }) => handleExpandButtonClick(isExpanded)}
          renderExpandButton={({ onClick, defaultLabel, ...rest }) => (
            <Button {...rest} key="showMore" variant="secondary" size="small" onClick={onClick}>
              {defaultLabel}
            </Button>
          )}
          css={styles.pathways}
          spacing={24}
        />
      </div>
    );
  };

  return (
    <section css={styles.section}>
      <PageGridContainer>
        <Grid item xs={12}>
          <Typography2
            component="h2"
            variant="titleSmall"
            css={styles.sectionTitle}
            data-testid="recommended-credentials"
          >
            {_t('Recommended credentials')}
          </Typography2>
          {renderFilterChips()}
          {renderPathways(selectedLevel)}
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default PathwaysModuleRenderer;
